import { useAuth } from "@/auth/auth.ts";
import useSWR, { unstable_serialize, useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";
import type { BazelBuildLog, GradleBuildLog, XcodeBuildLog } from "@limrun/api";
import { orgRequestOptions } from "./org.ts";
import { platformFromInstanceId } from "@/lib/instance-platform.ts";

// Re-exported so consumers keep importing the build-log types alongside the
// hooks and persisted-log parsing below.
export type { BazelBuildLog } from "@limrun/api";

// InstanceBuildLog is one persisted build record regardless of which daemon
// produced it; the xcode and gradle director responses share the same shape
// (the SDK aliases both names to one BuildLog interface).
export type InstanceBuildLog = XcodeBuildLog | GradleBuildLog;

// BazelInvocationMeta is the full metadata sidecar of a Bazel build record,
// mirroring limbuild's BazelInvocationSummary. The director's list response is
// thin, so the rich metrics are fetched separately when the log view opens.
// Metrics blocks are absent for a running build.
export type BazelRunnerCount = { name: string; count: number };
export type BazelMnemonicCount = { name: string; count: number };
export type BazelActions = {
	total: number;
	cacheHits: number;
	cacheHitRate: number;
	runners?: BazelRunnerCount[];
	mnemonics?: BazelMnemonicCount[];
};
export type BazelTargets = { total: number; built: number; failed: number };
export type BazelArtifact = {
	label: string;
	name: string;
	digest: string;
	sizeBytes: number;
};
export type BazelInvocationMeta = {
	invocationId: string;
	status: string;
	pattern?: string[];
	startedAt?: string;
	durationMs?: number;
	cpuTimeMs?: number;
	targets?: BazelTargets;
	actions?: BazelActions;
	artifacts?: BazelArtifact[];
};

// BuildLogLine is the console's shared display representation. Persisted raw
// logs become one stdout chunk; live streams retain typed events.
export type BuildLogLine =
	| { type: "meta"; data: InstanceBuildLog | BazelInvocationMeta }
	| { type: "command" | "stdout" | "stderr"; data: string };

// useBuildLogs lists the instance's persisted build records from the director,
// dispatching on the instance's platform derived from its TID prefix (the two
// endpoints return the same record shape).
export function useBuildLogs({
	instanceId,
	organizationTid,
	enabled = true,
	refreshIntervalMs,
}: {
	instanceId: string | undefined;
	// Pinned per request, see orgRequestOptions.
	organizationTid?: string;
	enabled?: boolean;
	refreshIntervalMs?: number;
}): {
	data: InstanceBuildLog[] | "loading";
	error?: Error;
} {
	const auth = useAuth();
	const { data, error } = useSWR<InstanceBuildLog[]>(
		enabled && instanceId ? ["buildLogs", instanceId, organizationTid] : null,
		async () => {
			const id = instanceId as string;
			const options = orgRequestOptions(organizationTid);
			return platformFromInstanceId(id) === "gradle"
				? auth.regionClient.get<GradleBuildLog[]>(
						`/v1/gradle_instances/${encodeURIComponent(id)}/build_logs`,
						options,
					)
				: auth.regionClient.xcodeInstances.listBuildLogs(id, options);
		},
		{
			refreshInterval: refreshIntervalMs,
		},
	);
	return {
		data: data ?? "loading",
		error: error instanceof Error ? error : undefined,
	};
}

// useBazelBuildLogs lists persisted Bazel RBE invocations for the instance.
// It is gated by design: any failure (a director without the endpoint, or a
// transient error) resolves to an empty list rather than surfacing an error, so
// the build-logs page is byte-for-byte unchanged when the backend does not yet
// emit Bazel records or there are none.
export function useBazelBuildLogs({
	instanceId,
	organizationTid,
	enabled = true,
	refreshIntervalMs,
}: {
	instanceId: string | undefined;
	// Pinned per request, see orgRequestOptions.
	organizationTid?: string;
	enabled?: boolean;
	refreshIntervalMs?: number;
}): {
	data: BazelBuildLog[] | "loading";
} {
	const auth = useAuth();
	const { data } = useSWR<BazelBuildLog[]>(
		enabled && instanceId ? ["bazelBuildLogs", instanceId, organizationTid] : null,
		async () => {
			try {
				return await auth.regionClient.xcodeInstances.listBazelBuildLogs(
					instanceId as string,
					orgRequestOptions(organizationTid),
				);
			} catch {
				return [];
			}
		},
		{
			refreshInterval: refreshIntervalMs,
		},
	);
	return { data: data ?? "loading" };
}

// fetchBuildLogContent downloads a persisted plain-text log, preserving line
// endings so copy/download stay byte-for-byte stable. Prompted command lines
// are tagged for heuristic coloring in the terminal.
export async function fetchBuildLogContent(
	downloadUrl: string,
): Promise<BuildLogLine[]> {
	const res = await fetch(downloadUrl);
	if (!res.ok) {
		throw new Error(`failed to download build log: HTTP ${res.status}`);
	}
	const text = await res.text();
	const chunks = text.match(/[^\n]*\n|[^\n]+$/g) ?? [];
	return chunks.map((data): BuildLogLine => ({
		type: data.startsWith("$ ") ? "command" : "stdout",
		data,
	}));
}

export async function fetchBuildLogMetadata<T>(metadataDownloadUrl: string): Promise<T> {
	const res = await fetch(metadataDownloadUrl);
	if (!res.ok) {
		throw new Error(`failed to download build metadata: HTTP ${res.status}`);
	}
	return (await res.json()) as T;
}

// BazelBuild is one build the region's build plane recorded or is running. A
// running build has no download URLs until its record is written.
export type BazelBuild = Omit<
  BazelBuildLog,
  "downloadUrl" | "metadataDownloadUrl"
> & {
  downloadUrl?: string;
  metadataDownloadUrl?: string;
  // The region whose plane ran the build; its live stream lives there.
  region?: string;
};

// A finished build without its record yet keeps the list polling fast, but
// only for a while: a record that never arrives must not pin the poll.
const RECORD_GRACE_MS = 10 * 60_000;

function awaitsRecord(build: BazelBuild): boolean {
  if (build.status.toUpperCase() === "RUNNING") return true;
  return (
    !build.downloadUrl &&
    !!build.startedAt &&
    Date.now() - Date.parse(build.startedAt) < RECORD_GRACE_MS
  );
}

// useBazelBuilds lists the organization's build plane builds, newest first.
// It polls every 2 s while a build runs or awaits its record, so the record
// soon replaces a live stream, and every 10 s otherwise.
export function useBazelBuilds({
  organizationTid,
}: {
  // Pinned per request, see orgRequestOptions.
  organizationTid?: string;
}): { data: BazelBuild[] | "loading"; error?: Error } {
  const auth = useAuth();
  const { data, error } = useSWR<BazelBuild[]>(
    ["bazelBuilds", organizationTid],
    async () => {
      const builds = await auth.regionClient.get<BazelBuild[]>(
        "/v1/bazel_builds",
        orgRequestOptions(organizationTid),
      );
      // The director answers oldest first with running builds last.
      // An older director answers null for an organization without builds.
      return (builds ?? []).slice().reverse();
    },
    {
      refreshInterval: (builds) =>
        builds?.some(awaitsRecord) ? 2_000 : 10_000,
    },
  );
  return {
    data: data ?? "loading",
    error: error instanceof Error ? error : undefined,
  };
}

// A build's summary and output never change once written, so they are
// fetched once per build id; presigned URLs rotate on every list poll.
const bazelBuildMetaKey = (id: string) => ["bazelBuildMeta", id];

export function useBazelBuildMeta(
  build: BazelBuild | undefined,
): BazelInvocationMeta | undefined {
  const { data } = useSWRImmutable(
    build?.metadataDownloadUrl ? bazelBuildMetaKey(build.id) : null,
    () =>
      fetchBuildLogMetadata<BazelInvocationMeta>(build!.metadataDownloadUrl!),
  );
  return data;
}

export function useBazelBuildOutput(build: BazelBuild | undefined): {
  lines: BuildLogLine[] | "loading";
  error?: Error;
} {
  const { data, error } = useSWRImmutable(
    build?.downloadUrl ? ["bazelBuildOutput", build.id] : null,
    () => fetchBuildLogContent(build!.downloadUrl!),
  );
  return {
    lines: data ?? "loading",
    error: error instanceof Error ? error : undefined,
  };
}

// How many summaries the insights fetch at once.
const META_FETCH_CONCURRENCY = 6;

// useBazelBuildMetas gathers the summaries of finished builds for the insights.
// It reads the per-build cache, fetches only the missing summaries a few at a
// time, and keeps the previous totals on screen while it does.
export function useBazelBuildMetas(
  builds: BazelBuild[],
): Map<string, BazelInvocationMeta> | "loading" {
  const { cache, mutate } = useSWRConfig();
  const finished = builds.filter((b) => b.metadataDownloadUrl);
  const cached = (id: string) =>
    cache.get(unstable_serialize(bazelBuildMetaKey(id)))?.data as
      BazelInvocationMeta | undefined;
  const { data } = useSWR(
    finished.length ? ["bazelBuildMetas", ...finished.map((b) => b.id)] : null,
    async () => {
      const queue = finished.filter((b) => !cached(b.id));
      const worker = async () => {
        for (let b = queue.shift(); b; b = queue.shift()) {
          try {
            const meta = await fetchBuildLogMetadata<BazelInvocationMeta>(
              b.metadataDownloadUrl!,
            );
            await mutate(bazelBuildMetaKey(b.id), meta, { revalidate: false });
          } catch {
            // A missing summary leaves the build out of the totals.
          }
        }
      };
      await Promise.all(Array.from({ length: META_FETCH_CONCURRENCY }, worker));
      return new Map(
        finished.flatMap((b) => {
          const meta = cached(b.id);
          return meta ? [[b.id, meta] as const] : [];
        }),
      );
    },
    { revalidateOnFocus: false, keepPreviousData: true },
  );
  if (!finished.length) return new Map();
  return data ?? "loading";
}
